import '../common.scss';

import { Box, Typography } from '@mui/material';
import {
  ConvergeDetailAccordion,
  CustomizedTable,
  ErrorPageLayout,
  NewTypeAhead,
  PaperLayout,
  NewConvergeButton,
  createTextColumn,
} from '../../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { useState, useCallback } from 'react';
import {
  useGenerateLossRunReportsMutation,
  useListLossRunReportsQuery,
} from '@converge/graphql';
import { TableColumn } from '../../../../presentation';
import { useConvergeSnackBar } from '../../../../contexts';

import { InsuredEntityPageContainer } from '../insured-entity.page.container';
import { InsuredEntityProps } from '../overview/insured-entity.detail.overview';

interface LossRunFormData {
  lossRunType: string;
}

interface LossRunReport {
  filename: string;
  reportType: string;
  createdAt: string;
  createdByUser: string;
}

const lossRunTypeOptions = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL', label: 'External' },
  { value: 'BOTH', label: 'Both External and Internal' },
];

export const InsuredEntityDetailReports = () => {
  const { insuredEntity } = useOutletContext<InsuredEntityProps>();
  const [isLossRunExpanded, setIsLossRunExpanded] = useState(true);
  const { notify } = useConvergeSnackBar();

  const { generateLossRunReports, loading: generatingReport } =
    useGenerateLossRunReportsMutation();

  const {
    data: reportsData,
    loading: loadingReports,
    error: reportsError,
    refetch: refetchReports,
  } = useListLossRunReportsQuery(insuredEntity?.uuid || '', {
    fetchPolicy: 'cache-and-network',
    skip: !insuredEntity?.uuid,
  });

  const formMethods = useForm<LossRunFormData>({
    defaultValues: {
      lossRunType: '',
    },
  });

  const { watch } = formMethods;
  const selectedLossRunType = watch('lossRunType');

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setIsLossRunExpanded(isExpanded);
  };

  const handleGenerateReport = useCallback(async () => {
    if (!insuredEntity?.uuid || !selectedLossRunType) {
      return;
    }

    try {
      const result = await generateLossRunReports({
        variables: {
          entityUuid: insuredEntity.uuid,
          reportType: selectedLossRunType, // Will be typed correctly after codegen
        },
      });

      if (result.data) {
        notify('Loss run report generated successfully!', {
          variant: 'success',
        });
        // Refetch the reports list to show the new report
        refetchReports();
      }
    } catch (error) {
      notify('Failed to generate loss run report. Please try again.', {
        variant: 'error',
      });
    }
  }, [
    insuredEntity?.uuid,
    selectedLossRunType,
    generateLossRunReports,
    notify,
    refetchReports,
  ]);

  if (!insuredEntity) {
    return (
      <ErrorPageLayout
        message="No insured data found"
        messageId="insured.detail.page.insuredEntityNotFound"
      />
    );
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const columns: TableColumn<LossRunReport>[] = [
    createTextColumn<LossRunReport>('filename', 'FILE NAME', {
      sortable: true,
      flex: 1.8,
      render: (value, row) => {
        const truncateNum = 32;
        const filename = String(value);
        const truncateFilename = (name: string) => {
          if (name.length <= truncateNum) return name;
          const extension = name.substring(name.lastIndexOf('.'));
          const nameWithoutExt = name.substring(0, name.lastIndexOf('.'));
          return `${nameWithoutExt.substring(0, truncateNum)}...${extension}`;
        };

        return (
          <a
            href={`${reportsData?.listLossRunReports?.storageBaseUrl || ''}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#000000',
              textDecoration: 'underline',
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontWeight: 600,
              fontSize: '14px',
              fontFamily: 'Red Hat Text',
            }}
          >
            {truncateFilename(filename)}
          </a>
        );
      },
    }),
    createTextColumn<LossRunReport>('reportType', 'TYPE', {
      sortable: true,
      flex: 0.4,
      render: (value) => {
        const typeMap: Record<string, string> = {
          INTERNAL: 'Internal',
          EXTERNAL: 'External',
          BOTH: 'Both',
        };
        const displayValue = typeMap[String(value)] || String(value);
        return <Typography className="table-cell">{displayValue}</Typography>;
      },
    }),
    createTextColumn<LossRunReport>('createdByUser', 'CREATOR', {
      sortable: true,
      flex: 1,
      render: (value) => {
        return (
          <Typography className="table-cell">
            {String(value) || 'Unknown'}
          </Typography>
        );
      },
    }),
    createTextColumn<LossRunReport>('createdAt', 'DATE', {
      sortable: true,
      flex: 1,
      render: (value) => (
        <Typography className="table-cell">
          {formatDate(String(value))}
        </Typography>
      ),
    }),
  ];

  return (
    <InsuredEntityPageContainer title="Reports">
      <PaperLayout className="insured-entity-page-layout">
        <div className="form-wrapper insured-reports">
          <ConvergeDetailAccordion
            expanded={isLossRunExpanded}
            onChange={handleAccordionChange}
            title="LOSS RUNS"
            description="Use this section to generate internal, external, or both types of loss run reports simultaneously. You can also view previously created reports for this insured."
            paddingBottom="30px"
            testId="loss-runs-accordion"
          >
            <FormProvider {...formMethods}>
              <Box paddingLeft={'26px'}>
                <Box display="flex" gap={4} flexWrap="wrap" sx={{ mb: 3 }}>
                  <Box sx={{ width: '220px' }}>
                    <NewTypeAhead
                      name="lossRunType"
                      label="LOSS RUN TYPE"
                      placeholder="Select Loss Run Type"
                      options={lossRunTypeOptions}
                      disabled={generatingReport}
                      clearable={false}
                    />
                  </Box>

                  <Box sx={{ marginTop: '24px' }}>
                    <NewConvergeButton
                      label={
                        generatingReport ? 'GENERATING...' : 'GENERATE LOSS RUN'
                      }
                      startIcon={''}
                      disabled={!selectedLossRunType || generatingReport}
                      onClick={handleGenerateReport}
                    />
                  </Box>
                </Box>
                <Box sx={{ width: '100%', minWidth: '800px' }}>
                  <CustomizedTable
                    data={reportsData?.listLossRunReports?.reports || []}
                    columns={columns}
                    loading={loadingReports}
                    message={
                      reportsError
                        ? 'Error loading reports. Please try again.'
                        : 'There are no loss run reports associated with this insured.'
                    }
                    enableSorting={true}
                  />
                </Box>
              </Box>
            </FormProvider>
          </ConvergeDetailAccordion>
        </div>
      </PaperLayout>
    </InsuredEntityPageContainer>
  );
};
